import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";

/* =========================================================
   HELPERS
========================================================= */

const inr = (value) => {
  return `₹${Number(value || 0).toLocaleString(
    "en-IN"
  )}`;
};

const safeNumber = (
  value,
  fallback = 0
) => {
  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : fallback;
};

/* =========================================================
   RAZORPAY
   Intentionally left as-is for this pass.
========================================================= */

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      return resolve(true);
    }

    const script =
      document.createElement("script");

    script.src =
      "https://checkout.razorpay.com/v1/checkout.js";

    script.onload = () => resolve(true);

    script.onerror = () =>
      resolve(false);

    document.body.appendChild(
      script
    );
  });
}

/* =========================================================
   CHECKOUT
========================================================= */

export default function Checkout() {
  const {
    items,
    total,
    clearCart,
  } = useCart();

  const navigate = useNavigate();

  const [schools, setSchools] =
    useState([]);

  const [form, setForm] = useState({
    customer_name: "",
    phone: "",
    email: "",
    address: "",
    school_id: "",
  });

  const [processing, setProcessing] =
    useState(false);

  const [serverSummary, setServerSummary] =
    useState(null);

  /* =======================================================
     LOAD SCHOOLS
  ======================================================= */

  useEffect(() => {
    let cancelled = false;

    const loadSchools = async () => {
      try {
        const response =
          await api.get("/schools");

        if (cancelled) {
          return;
        }

        const data = Array.isArray(
          response.data
        )
          ? response.data
          : [];

        setSchools(data);
      } catch (error) {
        if (!cancelled) {
          setSchools([]);

          console.error(
            "Could not load schools:",
            error
          );
        }
      }
    };

    loadSchools();

    return () => {
      cancelled = true;
    };
  }, []);

  /* =======================================================
     AUTO SELECT SCHOOL
  ======================================================= */

  useEffect(() => {
    /*
     * Only automatically select a school
     * when there is exactly one cart item.
     */
    if (
      form.school_id ||
      items.length !== 1
    ) {
      return;
    }

    const item = items[0];

    if (!item?.school_id) {
      return;
    }

    setForm((current) => ({
      ...current,
      school_id:
        item.school_id,
    }));
  }, [
    items,
    form.school_id,
  ]);

  /* =======================================================
     CART TOTAL
  ======================================================= */

  const cartSubtotal = useMemo(() => {
    return items.reduce(
      (sum, item) => {
        const price =
          safeNumber(
            item.price,
            0
          );

        const quantity =
          Math.max(
            0,
            safeNumber(
              item.quantity,
              0
            )
          );

        return (
          sum +
          price * quantity
        );
      },
      0
    );
  }, [items]);

  /*
   * Prefer CartContext's total when available,
   * but calculate independently as a safe fallback.
   */
  const browserSubtotal =
    safeNumber(
      total,
      cartSubtotal
    );

  /* =======================================================
     DISPLAYED SERVER VALUES
  ======================================================= */

  const displayedSubtotal =
    serverSummary
      ? safeNumber(
          serverSummary.subtotal,
          browserSubtotal
        )
      : browserSubtotal;

  const displayedDiscount =
    serverSummary
      ? Math.max(
          0,
          safeNumber(
            serverSummary.discount_amount,
            0
          )
        )
      : 0;

  const displayedTotal =
    serverSummary
      ? Math.max(
          0,
          safeNumber(
            serverSummary.total,
            browserSubtotal
          )
        )
      : browserSubtotal;

  /* =======================================================
     FORM UPDATE
  ======================================================= */

  const updateForm = (
    field,
    value
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  /* =======================================================
     PLACE ORDER
  ======================================================= */

  const placeOrder = async (
    event
  ) => {
    event.preventDefault();

    if (processing) {
      return;
    }

    /* =====================================================
       BASIC CART CHECK
    ===================================================== */

    if (!items.length) {
      toast.error(
        "Your bag is empty"
      );

      return;
    }

    /* =====================================================
       CUSTOMER NAME
    ===================================================== */

    const customerName =
      form.customer_name.trim();

    if (!customerName) {
      toast.error(
        "Please enter your full name"
      );

      return;
    }

    /* =====================================================
       PHONE
    ===================================================== */

    const phone =
      form.phone.replace(
        /\D/g,
        ""
      );

    if (
      !/^[6-9]\d{9}$/.test(phone)
    ) {
      toast.error(
        "Enter a valid 10-digit Indian mobile number"
      );

      return;
    }

    /* =====================================================
       ADDRESS
    ===================================================== */

    const address =
      form.address.trim();

    if (!address) {
      toast.error(
        "Please enter your delivery address"
      );

      return;
    }

    /* =====================================================
       SCHOOL
    ===================================================== */

    if (!form.school_id) {
      toast.error(
        "Please select your school"
      );

      return;
    }

    /* =====================================================
       VALIDATE CART ITEMS
    ===================================================== */

    const invalidItem =
      items.find((item) => {
        if (!item?.product_id) {
          return true;
        }

        const quantity =
          safeNumber(
            item.quantity,
            0
          );

        if (
          quantity < 1 ||
          !Number.isInteger(
            quantity
          )
        ) {
          return true;
        }

        /*
         * If a size exists in the cart,
         * send it. Otherwise null is valid
         * for products without variants.
         */
        return false;
      });

    if (invalidItem) {
      toast.error(
        "One or more cart items are invalid. Please remove them and add them again."
      );

      return;
    }

    /* =====================================================
       START PROCESSING
    ===================================================== */

    setProcessing(true);

    try {
      /*
       * IMPORTANT:
       *
       * The browser does NOT send:
       *
       * - price
       * - subtotal
       * - total
       * - discount_amount
       * - student_id
       * - payment_status
       *
       * The backend calculates these.
       */

      const payload = {
        customer_name:
          customerName,

        phone,

        email:
          form.email.trim(),

        address,

        school_id:
          form.school_id,

        items: items.map(
          (item) => ({
            product_id:
              item.product_id,

            quantity:
              Math.max(
                1,
                Math.floor(
                  safeNumber(
                    item.quantity,
                    1
                  )
                )
              ),

            size:
              item.size ||
              null,
          })
        ),
      };

      /* ===================================================
         CREATE ORDER
      =================================================== */

      const response =
        await api.post(
          "/orders",
          payload
        );

      const data =
        response?.data;

 	console.log("ORDER API RESPONSE:", data);

      if (
        !data ||
        !data.order_id ||
        !data.order_number
      ) {
        throw new Error(
          "The server returned an invalid order response."
        );
      }

      /*
       * Store server-calculated values.
       */
      setServerSummary(data);

      /* ===================================================
         RAZORPAY
         Left intentionally unchanged.
      =================================================== */

     if (!data.payment_enabled || !data.razorpay_order_id) {
  console.error("Razorpay was not enabled by the server:", data);

  toast.error(
    "Razorpay payment is currently unavailable. Please check the payment configuration."
  );

  setProcessing(false);
  return;
}

{
        const ok =
          await loadRazorpay();

        if (!ok) {
          toast.error(
            "Could not load payment gateway"
          );

          setProcessing(false);

          return;
        }

        const rzp =
          new window.Razorpay({
            key:
              data.razorpay_key_id,

            amount:
              data.amount,

            currency:
              data.currency,

            order_id:
              data.razorpay_order_id,

            name:
              "Uniform Co.",

            description:
              `School uniform order ${data.order_number}`,

            prefill: {
              name:
                customerName,

              email:
                form.email.trim(),

              contact:
                phone,
            },

            theme: {
              color:
                "#C25934",
            },

            handler:
              async (
                razorpayResponse
              ) => {
                try {
                  const verified =
                    await api.post(
                      "/orders/verify",
                      {
                        order_id:
                          data.order_id,

                        razorpay_order_id:
                          razorpayResponse.razorpay_order_id,

                        razorpay_payment_id:
                          razorpayResponse.razorpay_payment_id,

                        razorpay_signature:
                          razorpayResponse.razorpay_signature,
                      }
                    );

                  clearCart();

                  navigate(
                    "/order-success",
                    {
                      state: {
                        orderNumber:
                          data.order_number,

                        total:
                          data.total,

                        discountAmount:
                          data.discount_amount,

                        paymentStatus:
                          verified.data
                            .status,
                      },
                    }
                  );
                } catch (error) {
                  toast.error(
                    formatApiError(
                      error
                        ?.response
                        ?.data
                        ?.detail
                    ) ||
                      "Payment verification failed"
                  );

                  setProcessing(false);
                }
              },

            modal: {
              ondismiss:
                () => {
                  setProcessing(
                    false
                  );
                },
            },
          });

        rzp.open();

        return;
      }

      /* ===================================================
         NORMAL ORDER FLOW
      =================================================== */

      clearCart();

      navigate(
        "/order-success",
        {
          state: {
            orderNumber:
              data.order_number,

            total:
              data.total,

            subtotal:
              data.subtotal,

            discountAmount:
              data.discount_amount,

            discountPercent:
              data.student_discount_percent,

            paymentStatus:
              data.payment_status ||
              "pending",
          },
        }
      );
    } catch (error) {
      console.error(
        "Checkout error:",
        error
      );

      toast.error(
        formatApiError(
          error
            ?.response
            ?.data
            ?.detail
        ) ||
          error?.message ||
          "Could not place order"
      );

      setProcessing(false);
    }
  };

  /* =======================================================
     EMPTY CART
  ======================================================= */

  if (!items.length) {
    return (
      <div className="pt-28 min-h-screen">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-28">
          <div className="mb-10">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
              Final step
            </p>

            <h1 className="text-4xl md:text-6xl tracking-tight">
              Checkout
            </h1>
          </div>

          <div className="border border-border p-8 text-center">
            <p className="text-muted-foreground">
              Your bag is empty.
            </p>

            <button
              type="button"
              onClick={() =>
                navigate("/shop")
              }
              className="mt-5 underline text-sm"
            >
              Return to shop
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="pt-28 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-28">

        {/* =================================================
            HEADER
        ================================================= */}

        <div className="mb-10">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
            Final step
          </p>

          <h1 className="text-4xl md:text-6xl tracking-tight">
            Checkout
          </h1>
        </div>

        {/* =================================================
            MAIN GRID
        ================================================= */}

        <div className="grid lg:grid-cols-[1fr_440px] gap-14">

          {/* =================================================
              FORM
          ================================================= */}

          <form
            onSubmit={placeOrder}
            className="space-y-6"
            data-testid="checkout-form"
          >
            <p className="text-xs uppercase tracking-[.2em] text-muted-foreground">
              Customer details
            </p>

            {/* NAME */}

            <input
              value={
                form.customer_name
              }
              onChange={(event) =>
                updateForm(
                  "customer_name",
                  event.target.value
                )
              }
              placeholder="Full name"
              autoComplete="name"
              className="w-full bg-transparent border-b border-border py-4 outline-none focus:border-accent"
              required
            />

            {/* PHONE */}

            <input
              value={
                form.phone
              }
              onChange={(event) =>
                updateForm(
                  "phone",
                  event.target.value.replace(
                    /[^\d+ ]/g,
                    ""
                  )
                )
              }
              placeholder="10-digit phone number"
              inputMode="tel"
              autoComplete="tel"
              maxLength={14}
              className="w-full bg-transparent border-b border-border py-4 outline-none focus:border-accent"
              required
            />

            {/* EMAIL */}

            <input
              value={
                form.email
              }
              onChange={(event) =>
                updateForm(
                  "email",
                  event.target.value
                )
              }
              type="email"
              placeholder="Email (optional)"
              autoComplete="email"
              className="w-full bg-transparent border-b border-border py-4 outline-none focus:border-accent"
            />

            {/* SCHOOL */}

            <select
              value={
                form.school_id
              }
              onChange={(event) =>
                updateForm(
                  "school_id",
                  event.target.value
                )
              }
              className="w-full bg-background border border-border p-4 outline-none"
              required
            >
              <option value="">
                Select school
              </option>

              {schools.map(
                (school) => (
                  <option
                    key={
                      school.id
                    }
                    value={
                      school.id
                    }
                  >
                    {school.name}
                  </option>
                )
              )}
            </select>

            {/* ADDRESS */}

            <textarea
              value={
                form.address
              }
              onChange={(event) =>
                updateForm(
                  "address",
                  event.target.value
                )
              }
              placeholder="Delivery address / school delivery details"
              autoComplete="street-address"
              rows={4}
              className="w-full bg-transparent border border-border p-4 outline-none focus:border-accent resize-none"
              required
            />

            {/* SUBMIT */}

            <button
              type="submit"
              disabled={
                processing
              }
              className="w-full rounded-full bg-accent text-accent-foreground py-4 text-sm uppercase tracking-widest hover:bg-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processing
                ? "Processing…"
                : `Place order · ${inr(
                    displayedTotal
                  )}`}
            </button>
          </form>

          {/* =================================================
              ORDER SUMMARY
          ================================================= */}

          <aside className="border border-border p-6 h-fit lg:sticky lg:top-28">

            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-6">
              Order summary
            </p>

            {/* ITEMS */}

            {items.map(
              (item) => {
                const price =
                  safeNumber(
                    item.price,
                    0
                  );

                const quantity =
                  Math.max(
                    0,
                    safeNumber(
                      item.quantity,
                      0
                    )
                  );

                const lineTotal =
                  price *
                  quantity;

                return (
                  <div
                    key={
                      item.key
                    }
                    className="flex gap-3 py-4 border-b border-border"
                  >
                    {/* IMAGE */}

                    <div className="w-14 h-16 bg-secondary overflow-hidden shrink-0">
                      {item.image ? (
                        <img
                          src={
                            item.image
                          }
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : null}
                    </div>

                    {/* DETAILS */}

                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">
                        {
                          item.name
                        }
                      </p>

                      {item.school_name && (
                        <p className="text-xs text-muted-foreground truncate">
                          {
                            item.school_name
                          }
                        </p>
                      )}

                      <p className="text-xs text-muted-foreground">
                        {item.size &&
                          `Size: ${item.size}`}

                        {item.size &&
                          " · "}

                        ×{" "}
                        {
                          quantity
                        }
                      </p>
                    </div>

                    {/* PRICE */}

                    <span className="text-sm shrink-0">
                      {inr(
                        lineTotal
                      )}
                    </span>
                  </div>
                );
              }
            )}

            {/* SUBTOTAL */}

            <div className="flex justify-between pt-5 text-sm">
              <span>
                Subtotal
              </span>

              <span>
                {inr(
                  displayedSubtotal
                )}
              </span>
            </div>

            {/* DISCOUNT */}

            {displayedDiscount >
              0 && (
              <div className="flex justify-between pt-3 text-sm text-green-700">
                <span>
                  Student discount

                  {serverSummary?.student_discount_percent
                    ? ` (${serverSummary.student_discount_percent}%)`
                    : ""}
                </span>

                <span>
                  -{inr(
                    displayedDiscount
                  )}
                </span>
              </div>
            )}

            {/* TOTAL */}

            <div className="flex justify-between pt-5 mt-4 border-t border-border text-lg">
              <span>
                Total
              </span>

              <span>
                {inr(
                  displayedTotal
                )}
              </span>
            </div>

            {/* SECURITY NOTE */}

            <p className="text-xs text-muted-foreground mt-4 leading-5">
              If the phone number belongs
              to an active registered
              student, the eligible
              student discount is
              calculated securely on the
              server. Product prices,
              discounts and totals sent
              from the browser are not
              trusted.
            </p>

          </aside>
        </div>
      </div>
    </div>
  );
}
